import type { FastFeedBuyerRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

function truncAddr(addr: string, chars = 6): string {
  if (addr.length <= chars * 2 + 2) return addr;
  return `${addr.slice(0, chars)}…${addr.slice(-4)}`;
}

function ChartInner({
  rows,
  cc,
}: { rows: FastFeedBuyerRow[]; cc: ReturnType<typeof useChartColors> }) {
  const top = rows.slice(0, 10);

  const option = useMemo<EChartsOption>(() => {
    const labels = top.map((r) => truncAddr(r.buyer_address));
    const values = top.map((r) => +r.total_usdg.toFixed(2));

    return {
      grid: { left: 100, right: 60, top: 12, bottom: 12 },
      xAxis: {
        type: "value",
        axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `$${v.toFixed(0)}` },
        splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
      },
      yAxis: {
        type: "category",
        data: [...labels].reverse(),
        axisLabel: {
          fontSize: 10,
          color: cc.text,
          fontFamily: "monospace",
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (p: unknown) => {
          const params = p as Array<{ dataIndex: number; value: number }>;
          if (!params[0]) return "";
          const r = top[top.length - 1 - params[0].dataIndex];
          if (!r) return "";
          return `<b>${r.buyer_address}</b><br/>Total: $${r.total_usdg.toFixed(2)}<br/>Share: ${r.share_pct.toFixed(1)}%<br/>Payments: ${r.payment_count}`;
        },
      },
      series: [
        {
          type: "bar",
          data: [...values].reverse(),
          barMaxWidth: 18,
          label: {
            show: true,
            position: "right",
            fontSize: 10,
            color: cc.text,
            formatter: (p: unknown) => {
              const val = (p as { value: number }).value;
              return `${top.find((r) => +r.total_usdg.toFixed(2) === val)?.share_pct.toFixed(1) ?? ""}%`;
            },
          },
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: "rgba(20,184,166,0.4)" },
                { offset: 1, color: "#14b8a6" },
              ],
            },
            borderRadius: [0, 3, 3, 0],
          },
        },
      ],
    };
  }, [top, cc]);

  const height = Math.max(180, top.length * 28 + 24);
  const ref = useECharts(option, [option]);
  return <div ref={ref} style={{ height }} />;
}

interface Props {
  rows: FastFeedBuyerRow[];
  loading?: boolean;
}

export function FastFeedTopBuyersChart({ rows, loading = false }: Props) {
  const cc = useChartColors();
  return (
    <Panel title="TOP BUYERS (ALL-TIME)">
      {loading ? (
        <div className="skeleton" style={{ height: 280, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No buyer data
        </div>
      ) : (
        <ChartInner rows={rows} cc={cc} />
      )}
    </Panel>
  );
}
